import { HttpError } from '@forto-dev/ui-platform-http'
import { Customer } from 'domain/Customer'
import { User } from 'domain/User'
import { isThirdPartyFreightForwarderWithRestrictedPlatformAccess } from 'features/PurchaseOrders'
import { History } from 'history'
import { jwtDecode } from 'jwt-decode'
import { find } from 'lodash-es'
import { action, observable } from 'mobx'
import { CustomErrorWithCause } from 'utils/error'
import { logError, logWarn } from 'utils/logger'
import { addRumScope, removeRumScope } from 'utils/Rum'

import { ActivateParamsType, API } from './API'
import { ErrorService } from './ErrorService'
import { InitialDataResJSON } from './internal/InitialData'
import { UserShipmentMetadata } from './internal/UserShipmentMetadata'
import { UserStore } from './internal/UserStore'
import { authorize, hasRoles } from './roles'

interface Props {
  API: API
  history: History
  errorService: ErrorService
  storage: Storage
}

interface JwtTokenData {
  customerId: string
  userId: string
  loggedInAsCustomer?: boolean
  // indicates where the login was initiated from
  tokenRequestedFrom?: string
}

export class AuthStore {
  state = observable({
    token: '',
    authenticated: false,
    loggedInAsCustomer: false,
    customerLoaded: false,
    // @ts-expect-error TS(2352) FIXME: Conversion of type 'null' to type 'Customer' may b... Remove this comment to see the full error message
    customer: null as Customer,
    // @ts-expect-error TS(2352) FIXME: Conversion of type 'null' to type 'UserStore' may ... Remove this comment to see the full error message
    user: null as UserStore,
    socketUserToken: '',
    shipmentToken: '',
    mainOrganization: '',
    // @ts-expect-error TS(2352) FIXME: Conversion of type 'null' to type 'UserShipmentMet... Remove this comment to see the full error message
    shipmentMetadata: null as UserShipmentMetadata,
    initializing: false,
    tokenRequestedFrom: '',
  })

  API: API
  history: History
  errorService: ErrorService
  storage: Storage

  constructor(props: Props) {
    this.storage = props.storage
    this.API = props.API
    this.history = props.history
    this.errorService = props.errorService
  }

  init = () => {
    const token = this.storage.getItem('token')
    if (!token) {
      this.setUnauthenticated()
      // HOTFIX: Don't load customer if trying to login as customer from TMS. this.loginAs will be called instead
      // TODO: clean up
    } else if (!window.location.pathname.includes('loginas')) {
      this.initLoadCustomer(token).catch((err) => logError(err))
    }
  }

  initLoadCustomer = async (token: string) => {
    this.setAuthenticated(token)
    await this.requireAuth()
  }

  authenticate = (email, password) => {
    return this.API.authenticate(email, password).then(this.onAuthenticated)
  }

  activate = (activateParams: ActivateParamsType) => {
    return this.API.customers.activate(activateParams).then(this.onAuthenticated)
  }

  private onAuthenticated = async (data: { token: string }) => {
    await this.initLoadCustomer(data.token)
    return Promise.resolve()
  }

  loadShipmentToken = (shipmentId: string) => {
    return this.API.shipments.getShipmentToken(shipmentId).then((res) => {
      this.state.shipmentToken = res.token
      return Promise.resolve(res.token)
    })
  }

  private loadSocketUserToken = (): Promise<string> => {
    return this.API.users.getUserToken().then((res) => {
      this.state.socketUserToken = res.token
      return Promise.resolve(res.token)
    })
  }

  requireAuth = async (): Promise<void> => {
    if (this.state.token) {
      if (!this.state.customer && !this.state.initializing) {
        await this.loadCustomer()
      }
    } else {
      this.history.replace({
        pathname: '/login',
        state: {
          nextPathname: this.history.location.pathname,
        },
      })
    }
  }

  @action
  reloadCustomer = async (): Promise<void> => {
    return this.loadCustomer()
  }

  @action
  private setCustomer = (customer: Customer, user: User) => {
    this.state.customer = customer
    this.state.user = new UserStore(user)

    addRumScope({
      id: user._id,
      email: user.email,
      company: customer.name,
      customerId: customer._id,
    })
  }

  @action
  private unsetCustomer = () => {
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'Customer'.
    this.state.customer = null
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'UserStore'.
    this.state.user = null
    this.state.customerLoaded = false
    this.unsetAsCustomer()
    removeRumScope()
  }

  @action
  private setUserShipmentMetadata = (shipmentMetadata: UserShipmentMetadata) => {
    this.state.shipmentMetadata = shipmentMetadata
  }

  @action
  private unsetUserShipmentMetadata = () => {
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'UserShipmen... Remove this comment to see the full error message
    this.state.shipmentMetadata = null
  }

  @action
  private setAuthenticated = (token: string) => {
    if (token.indexOf('JWT ') === 0) {
      // clean token from the scheme name
      token = token.substr(4)
    }
    const tokenData = jwtDecode<JwtTokenData>(token)
    // A third party FF should only be able to access fortox, not ship. We thus immediatly unset their token again.
    if (isThirdPartyFreightForwarderWithRestrictedPlatformAccess(tokenData.customerId)) {
      this.setUnauthenticated()
      // history.replace does not work here (likely because we are redirecting to a different root url)
      window.location.replace(`${window.config.FORTO_X_URL}/login`)
      return
    }
    this.state.authenticated = true
    this.state.token = token
    this.state.loggedInAsCustomer = Boolean(tokenData.loggedInAsCustomer)
    this.state.tokenRequestedFrom = String(tokenData.tokenRequestedFrom)
    this.storage.setItem('token', token)
    this.setCookie()
  }

  @action
  private setCookie = () => {
    const { token } = this.state
    try {
      // Temporary auth bridge for beta Ship app https://forto.atlassian.net/browse/UWG-2077
      // Sets auth token at top-level domain (e.g., forto.com) for cross-domain access
      // TODO: Remove once permanent auth solution is implemented before beta launch

      logWarn('Attempting optional cross-domain auth setup')

      // Guard clause - silently exit if we're missing core requirements
      if (!token) {
        logWarn('No token available for cross-domain auth - skipping')
        return
      }

      // Validate domain structure
      const domains = document.location.host.split('.')
      if (domains.length < 2) {
        logWarn('Invalid domain format for cross-domain auth - skipping')
        return // Exit gracefully instead of throwing error
      }

      // Extract and validate main domain
      domains.shift()
      const mainDomain = domains.join('.')
      if (!mainDomain.includes('.')) {
        logWarn('Unable to determine valid parent domain - skipping')
        return
      }

      // Attempt to set the cookie
      try {
        const expirationDate = new Date(Date.now() + 5 * 60000).toUTCString()
        document.cookie = `shipToken=${token}; Domain=.${mainDomain}; Path=/; Expires=${expirationDate}`
        logWarn(`Successfully set cross-domain auth cookie for: ${mainDomain}`)
      } catch (cookieError) {
        // Cookie setting can fail due to browser security settings or other reasons
        logWarn('Failed to set cross-domain cookie - continuing without it')
        logError(cookieError as Error)
        // Don't rethrow - this is non-blocking
      }
    } catch (error) {
      // Catch any unexpected errors to ensure main auth flow isn't affected
      logWarn('Unexpected error in cross-domain auth setup - continuing')
      logError(error as Error)
      // Continue execution - don't block main flow
    }
  }

  @action
  private setUnauthenticated = () => {
    this.state.authenticated = false
    this.state.token = ''
    this.storage.removeItem('token')
  }

  isAuthenticated = () => {
    return this.state.authenticated
  }

  @action
  loginAs = async (jwtToken: string, redirectTo) => {
    if (this.state.authenticated) {
      this.unauthenticate()
    }
    this.setAsCustomer()
    this.state.initializing = false
    await this.initLoadCustomer(jwtToken)
    this.history.replace(redirectTo ?? '/')
  }

  @action
  private setAsCustomer = () => {
    this.state.loggedInAsCustomer = true
  }

  @action
  private unsetAsCustomer = () => {
    this.state.loggedInAsCustomer = false
  }

  @action
  unauthenticate = () => {
    this.unsetCustomer()
    this.unsetUserShipmentMetadata()
    this.setUnauthenticated()
  }

  @action
  logout = () => {
    this.unauthenticate()
    this.history.replace('/login')
  }

  @action
  private onCustomerLoaded = ({
    initialData,
    socketUserToken,
  }: {
    initialData: InitialDataResJSON
    socketUserToken: string
  }) => {
    try {
      const { customer, shipmentMetadata } = initialData
      this.state.socketUserToken = socketUserToken
      const tokenData = jwtDecode<JwtTokenData>(this.state.token)
      const user: User = customer.users.filter((u) => {
        return u._id === tokenData.userId
      })[0]
      this.setCustomer(customer, user)
      this.setUserShipmentMetadata(shipmentMetadata)
      this.state.customerLoaded = true
    } catch {
      this.errorService.showGeneralError()
    }
  }

  private onServerError = (consoleMessage: string, serverError: HttpError): void => {
    /**
     * Only triggering the logout if it's a server response that causes the request to fail, not a network error or a
     * quick navigation that cancels the request, like what happens with login-as.
     * The experience is not perfect, since it'll be stuck on the loading screen if you have a network issue, but it
     * fixes the issue where you get logged out immediately if you are redirected to ship-v2
     * */
    if (serverError?.status) {
      this.logout()
    }
    /** Temporary workaround to clean unauthorized errors from logs
     * @todo Move all error logging to HTTP service */
    if (serverError?.status !== 401) {
      logError(new CustomErrorWithCause(consoleMessage, { cause: serverError }))
    }
  }

  get isDisguised(): boolean {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('entity') === 'freightie'
  }

  @action
  private loadMainOrganization = async (): Promise<void> => {
    try {
      const organizationsRes = await this.API.network.organization.getAllOrganizations()
      const mainOrg = find(organizationsRes.data, (org) => org.isMain)
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      this.state.mainOrganization = mainOrg._id
    } catch (serverError) {
      this.onServerError('Organizations load error', serverError as HttpError)
    }
  }

  private loadCustomer = async (): Promise<void> => {
    try {
      this.state.initializing = true
      const [initialData, socketUserToken] = await Promise.all([
        this.API.initial.getInitialData(),
        this.loadSocketUserToken(),
      ])
      this.onCustomerLoaded({
        initialData,
        socketUserToken,
      })
      if (hasRoles(this.state.user, authorize.managers)) {
        await this.loadMainOrganization()
      }
    } catch (serverError) {
      this.onServerError('Customer load error', serverError as HttpError)
    } finally {
      this.state.initializing = false
    }
  }
}
